import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'use-intl';
import { Body, Button, Caption, Row, Stack } from '@leanin/ui';

import { LoadErrorState, LoadingState } from '@/components/states/index';
import { useAdminBroadcastAudienceTargets } from '@/lib/queries/admin';
import {
  useUpdateBroadcast,
  type UpdateBroadcastInput,
} from '@/lib/mutations/admin';
import {
  useUpdateGroupBroadcast,
  type UpdateGroupBroadcastInput,
} from '@/lib/mutations/groups';
import { DomainError } from '@leanin/shared/errors';
import { useErrorMessage } from '@/lib/error-message';
import { useStepUp } from '@/lib/use-step-up';
import {
  broadcastKeys,
  type AdminBroadcastDetail,
  type BroadcastScope,
} from '@/lib/queries/broadcasts';
import { emptyBroadcastDoc } from './BroadcastBodyEditor';
import {
  BroadcastEditorFields,
  EMPTY_AUDIENCE_STATE,
  audiencePatch,
  audienceStateFromSpec,
  type AudienceState,
  type BroadcastAudienceContext,
} from './BroadcastEditorFields';
import {
  contentDraftFromDetail,
  contentDraftGap,
  contentPatchFromDraft,
  type BroadcastContentDraft,
} from './broadcast-content-draft';
import { ScheduleSendButton } from './ScheduleSendButton';
import { UnsavedChangesDialog } from './UnsavedChangesDialog';
import { useUnsavedChangesGuard } from '@/lib/use-unsaved-changes-guard';

export interface BroadcastEditFormProps {
  readonly audience: BroadcastAudienceContext;
  readonly draft: AdminBroadcastDetail;
  // True when this mount replaced a form whose save hit a version conflict:
  // the page remounts the form once the latest server copy has loaded, and
  // the member is told why their edits are gone.
  readonly reloadedAfterConflict: boolean;
  readonly onVersionConflict: () => void;
}

// Platform drafts can change their audience, so the form opens on the stored
// one — hand-picked ids are resolved to display rows first. Group drafts
// never edit the audience (the PATCH omits it), so there is nothing to load.
export function BroadcastEditForm(props: BroadcastEditFormProps) {
  return props.audience.mode === 'platform' ? (
    <PlatformBroadcastEditForm {...props} />
  ) : (
    <BroadcastEditFormBody {...props} initialAudienceState={EMPTY_AUDIENCE_STATE} />
  );
}

function PlatformBroadcastEditForm(props: BroadcastEditFormProps) {
  const tAdmin = useTranslations('Admin.broadcasts');
  const targetsQ = useAdminBroadcastAudienceTargets(props.draft.audienceSpec);
  if (targetsQ.isError) {
    return <LoadErrorState title={tAdmin('loadOneError')} error={targetsQ.error} />;
  }
  if (targetsQ.data === undefined) {
    return <LoadingState rows={3} label="Loading broadcast" />;
  }
  return (
    <BroadcastEditFormBody
      {...props}
      initialAudienceState={audienceStateFromSpec(props.draft.audienceSpec, targetsQ.data)}
    />
  );
}


function BroadcastEditFormBody({
  audience,
  draft,
  reloadedAfterConflict,
  onVersionConflict,
  initialAudienceState,
}: BroadcastEditFormProps & { readonly initialAudienceState: AudienceState }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const updatePlatform = useUpdateBroadcast();
  const updateGroup = useUpdateGroupBroadcast();
  const { runStepUp } = useStepUp();
  const tDm = useTranslations('Admin.broadcasts.dm');
  const tForm = useTranslations('Admin.broadcasts.form');
  const describeError = useErrorMessage();

  const [initialContent] = useState<BroadcastContentDraft>(() =>
    contentDraftFromDetail(draft, emptyBroadcastDoc),
  );
  const [content, setContent] = useState<BroadcastContentDraft>(initialContent);
  const [audienceState, setAudienceState] =
    useState<AudienceState>(initialAudienceState);
  const audienceTouched = audienceState !== initialAudienceState;
  const [error, setError] = useState<string | null>(
    reloadedAfterConflict ? tForm('conflictReloaded') : null,
  );
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [savePending, setSavePending] = useState(false);

  const [version, setVersion] = useState(draft.version);

  const pending = updatePlatform.isPending || updateGroup.isPending;

  const scope: BroadcastScope =
    audience.mode === 'platform'
      ? { kind: 'platform' }
      : {
          kind: 'group',
          groupKind: audience.kind,
          groupId: audience.groupId,
        };

  const dirty =
    audienceTouched || JSON.stringify(content) !== JSON.stringify(initialContent);

  const guard = useUnsavedChangesGuard({ dirty });

  const gap = contentDraftGap(content);
  const minimumFieldsForSave = gap === null;

  // 'cancelled' = user dismissed the step-up reverification prompt (platform
  // send/schedule only); 'invalid' carries client-side validation copy;
  // 'failed' carries the thrown API error, resolved to copy per intent.
  type SubmitResult =
    | { status: 'ok' }
    | { status: 'cancelled' }
    | { status: 'invalid'; message: string }
    | { status: 'failed'; error: unknown };

  const submit = async (
    mode: 'send' | 'schedule' | 'draft',
    scheduledIso?: string,
  ): Promise<SubmitResult> => {
    if (gap === 'subject') {
      return { status: 'invalid', message: tForm('subjectRequired') };
    }
    if (gap === 'message') {
      return { status: 'invalid', message: tForm('messageRequired') };
    }
    // 'send' = send now → schedule for the current instant. The server flips
    // status draft→scheduled off a non-null scheduledSendAt and the dispatcher
    // picks it up immediately (mirrors the composer's create-with-mode='send'
    // path). Previously 'send' fell through to `undefined`, so the PATCH
    // omitted scheduledSendAt and the draft's status never changed.
    const scheduledSendAt =
      mode === 'schedule'
        ? scheduledIso
        : mode === 'draft'
          ? null
          : new Date().toISOString();
    // A platform-wide send (draft→scheduled) is step-up'd server-side (RBAC
    // Decision C); runStepUp opens Clerk's reverification modal and retries
    // once on success. Group-leader sends carry no step-up — runStepUp is a
    // transparent pass-through there (the server never returns the hint), so
    // both branches share one normalised outcome.
    const runUpdate = () =>
      audience.mode === 'platform'
        ? runStepUp(() => {
            const patch: UpdateBroadcastInput = {
              version,
              ...contentPatchFromDraft(content, draft.sendVia),
              ...audiencePatch(initialAudienceState, audienceState),
              ...(scheduledSendAt !== undefined ? { scheduledSendAt } : {}),
            };
            return updatePlatform.mutateAsync({ id: draft.id, patch });
          })
        : runStepUp(() => {
            // Group broadcasts are email; the Channel control is platform-only.
            const { email } = content;
            const input: UpdateGroupBroadcastInput = {
              kind: audience.kind,
              groupId: audience.groupId,
              broadcastId: draft.id,
              version,
              subject: email.subject.trim(),
              bodyDoc: email.bodyDoc,
              preheaderText: email.preheaderText.trim() || null,
              replyTo: email.replyTo.trim() || null,
              ...(scheduledSendAt !== undefined ? { scheduledSendAt } : {}),
            };
            return updateGroup.mutateAsync(input);
          });

    let mutateOutcome: Awaited<ReturnType<typeof runUpdate>>;
    try {
      mutateOutcome = await runUpdate();
    } catch (error) {
      // A Clerk reverification failure other than the member cancelling.
      return { status: 'failed', error };
    }
    if (mutateOutcome.status === 'cancelled') return { status: 'cancelled' };
    if (mutateOutcome.status === 'error') {
      return { status: 'failed', error: mutateOutcome.error };
    }
    setVersion(mutateOutcome.data.version);
    return { status: 'ok' };
  };

  // A stale `version` is rejected as `CONFLICT` with reason
  // `admin_broadcast/version_mismatch`; the reason, not the code, names it.
  const isVersionConflict = (err: unknown): boolean =>
    err instanceof DomainError &&
    err.error.code === 'CONFLICT' &&
    err.error.reason === 'admin_broadcast/version_mismatch';

  // Resolves a non-ok result to the copy to show, or `null` when nothing
  // should render (cancelled reverification, or a version conflict that
  // reloads the draft and reports it in the form-level banner).
  const failureCopy = async (
    result: Exclude<SubmitResult, { status: 'ok' }>,
    fallback: string,
  ): Promise<string | null> => {
    if (result.status === 'cancelled') return null;
    if (result.status === 'invalid') return result.message;
    if (isVersionConflict(result.error)) {
      // The member's edits are about to be replaced by the server copy, so a
      // navigation held by the leave dialog is cancelled rather than left
      // pending across the remount: they stay to review the reloaded draft.
      guard.reset();
      setError(tForm('conflictReloading'));
      await qc.invalidateQueries({
        queryKey: broadcastKeys.detail(scope, draft.id).queryKey,
      });
      onVersionConflict();
      return null;
    }
    return describeError(result.error, fallback);
  };

  const navigateAway = () => {
    guard.allowNextNavigation();
    if (audience.mode === 'platform') {
      void navigate({ to: '/admin/broadcasts' });
    } else {
      window.history.back();
    }
  };

  const handleSendNow = async () => {
    setError(null);
    const result = await submit('send');
    if (result.status === 'ok') return navigateAway();
    const copy = await failureCopy(result, tForm('sendFailed'));
    if (copy !== null) setError(copy);
  };

  const handleSchedule = async (iso: string) => {
    setError(null);
    const result = await submit('schedule', iso);
    if (result.status === 'ok') return navigateAway();
    const copy = await failureCopy(result, tForm('scheduleFailed'));
    if (copy !== null) setError(copy);
  };

  const handleGuardSave = async () => {
    setDialogError(null);
    setSavePending(true);
    const result = await submit('draft');
    setSavePending(false);
    if (result.status === 'ok') return guard.proceed();
    const copy = await failureCopy(result, tForm('saveFailed'));
    if (copy !== null) setDialogError(copy);
  };

  const handleCancel = () => {
    if (audience.mode === 'platform') {
      void navigate({ to: '/admin/broadcasts' });
    } else {
      window.history.back();
    }
  };

  return (
    <Stack gap="lg">
      <BroadcastEditorFields
        audience={audience}
        content={content}
        onContentChange={setContent}
        audienceState={audienceState}
        onAudienceStateChange={setAudienceState}
        broadcastIdForPreview={draft.id}
        disabled={pending}
      />

      {error !== null ? (
        <Body size="sm" color="destructive">
          {error}
        </Body>
      ) : null}

      <Row gap="md" align="center" justify="space-between">
        <Caption>
          Version {version} · last updated{' '}
          {new Date(draft.createdAt).toLocaleString()}
        </Caption>
        <Row gap="md" align="center">
          <Button
            type="button"
            variant="ghost"
            size="md"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <ScheduleSendButton
            disabled={!minimumFieldsForSave}
            pending={pending}
            onSendNow={handleSendNow}
            onSchedule={handleSchedule}
          />
        </Row>
      </Row>

      <UnsavedChangesDialog
        open={guard.dialogOpen}
        onCancel={guard.reset}
        onSave={() => void handleGuardSave()}
        onDiscard={guard.proceed}
        saveDisabled={!minimumFieldsForSave}
        saveDisabledReason={
          minimumFieldsForSave
            ? undefined
            : content.channel === 'dm'
              ? tDm('saveDisabledReason')
              : tForm('draftNeedsContent')
        }
        savePending={savePending}
        error={dialogError}
      />
    </Stack>
  );
}

